import React, { useEffect, useRef, useState } from 'react';
import { FiCamera, FiTrash2, FiSend, FiX, FiEye } from 'react-icons/fi';

const API = process.env.REACT_APP_BACKEND_URL;

const VILLAS = [
  { id: '3bhk-garden-escape-lonavala', label: 'Artistic Villa (3BHK)' },
  { id: '4bhk-garden-retreat-lonavala', label: 'Reunion Villa (4BHK)' },
  { id: '5bhk-garden-delight-lonavala', label: 'Richie Villa (5BHK)' },
  { id: '12bhk-celebration-villa-lonavala', label: 'Celebration Villa (12BHK)' },
];

const MAX_PHOTOS = 20;
const JPEG_QUALITY = 0.7;
const MAX_DIM = 1280; // Downscale so files stay small (~250KB)

/** Compress an image blob via canvas — bounded by MAX_DIM. */
function compressImage(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > height && width > MAX_DIM) { height = height * (MAX_DIM / width); width = MAX_DIM; }
      else if (height > MAX_DIM) { width = width * (MAX_DIM / height); height = MAX_DIM; }
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

export function StaffPhotoCapture({ token }) {
  const [villaId, setVillaId] = useState('');
  const [photos, setPhotos] = useState([]); // [{id, data_url}]
  const [camOn, setCamOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [flash, setFlash] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const startCamera = async () => {
    setErr('');
    if (!villaId) return setErr('Select the villa first');
    if (photos.length >= MAX_PHOTOS) return setErr(`Max ${MAX_PHOTOS} photos reached — submit or delete some.`);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      setCamOn(true);
      // Attach on next tick after video element exists
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch (e) {
      setErr('Camera not available. Please allow camera permission.');
    }
  };

  const stopCamera = () => {
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (e) { /* noop */ }
    streamRef.current = null;
    setCamOn(false);
  };

  useEffect(() => () => stopCamera(), []);

  const snap = async () => {
    if (!videoRef.current || photos.length >= MAX_PHOTOS) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    const raw = canvas.toDataURL('image/jpeg', 0.92);
    const compressed = await compressImage(raw);
    setPhotos(p => [...p, { id: crypto.randomUUID?.() || `${Date.now()}-${p.length}`, data_url: compressed }]);
  };

  const removePhoto = (id) => setPhotos(p => p.filter(x => x.id !== id));

  const submit = async () => {
    setErr('');
    if (!villaId) return setErr('Select the villa first');
    if (!photos.length) return setErr('Click at least 1 photo before submitting');
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/feedback-app/${token}/villa-photos`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ villa_id: villaId, photos: photos.map(p => ({ data_url: p.data_url })) }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Upload failed'); }
      const d = await res.json();
      setPhotos([]); setVillaId(''); stopCamera();
      setFlash(`Sent ${d.count} photos to Host!`);
      setTimeout(() => setFlash(''), 3500);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="bg-[#1B2432] rounded-lg p-3 mt-3" data-testid="staff-photo-capture">
      <p className="text-[#F57C20] text-[10px] tracking-widest uppercase mb-2 font-semibold">Villa Readiness Photos</p>
      <p className="text-white/60 text-[10px] mb-2">Click up to {MAX_PHOTOS} photos per villa. Photos never save to your phone gallery — they go straight to the Host.</p>

      <select value={villaId} onChange={e => setVillaId(e.target.value)}
        data-testid="photo-villa-select" disabled={photos.length > 0}
        className="w-full bg-[#0F1420] text-white text-xs px-2 py-2 rounded border border-white/10 mb-2 disabled:opacity-60">
        <option value="">Select villa…</option>
        {VILLAS.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
      </select>

      {camOn && (
        <div className="relative bg-black rounded-lg overflow-hidden mb-2" data-testid="camera-view">
          <video ref={videoRef} playsInline muted className="w-full block" style={{ maxHeight: '60vh' }} />
          <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-4">
            <button onClick={stopCamera} data-testid="cam-close-btn"
              className="bg-white/90 text-[#1A1A2E] w-10 h-10 rounded-full flex items-center justify-center">
              <FiX className="w-5 h-5" />
            </button>
            <button onClick={snap} disabled={photos.length >= MAX_PHOTOS}
              data-testid="cam-snap-btn"
              className="bg-[#F57C20] w-14 h-14 rounded-full flex items-center justify-center ring-4 ring-white/80 disabled:opacity-40">
              <FiCamera className="w-6 h-6 text-white" />
            </button>
            <div className="bg-white/90 text-[#1A1A2E] text-xs font-bold w-10 h-10 rounded-full flex items-center justify-center">
              {photos.length}/{MAX_PHOTOS}
            </div>
          </div>
        </div>
      )}

      {!camOn && (
        <button onClick={startCamera}
          data-testid="open-camera-btn"
          className="w-full bg-[#F57C20] hover:bg-[#e56d10] text-white text-sm font-bold py-2.5 rounded flex items-center justify-center gap-2 mb-2 disabled:opacity-50"
          disabled={busy || photos.length >= MAX_PHOTOS}>
          <FiCamera className="w-4 h-4" /> {photos.length > 0 ? 'Add More Photos' : 'Open Camera'}
        </button>
      )}

      {photos.length > 0 && (
        <>
          <div className="grid grid-cols-4 gap-1 mb-2" data-testid="photo-thumbnails">
            {photos.map(p => (
              <div key={p.id} className="relative aspect-square">
                <img src={p.data_url} alt="" className="w-full h-full object-cover rounded" />
                <button onClick={() => removePhoto(p.id)}
                  data-testid={`remove-photo-${p.id}`}
                  className="absolute top-0.5 right-0.5 bg-black/70 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]">
                  <FiTrash2 className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
          </div>
          <button onClick={submit} disabled={busy}
            data-testid="submit-photos-btn"
            className="w-full bg-green-600 hover:bg-green-700 text-white text-sm font-bold py-2.5 rounded flex items-center justify-center gap-2 disabled:opacity-50">
            <FiSend className="w-4 h-4" /> {busy ? 'Uploading…' : `Send ${photos.length} Photos to Host`}
          </button>
        </>
      )}

      {err && <p className="text-red-400 text-[11px] mt-2" data-testid="photo-error">{err}</p>}
      {flash && <p className="text-green-400 text-[11px] mt-2" data-testid="photo-flash">{flash}</p>}
    </div>
  );
}

export function HostPhotosGallery({ token }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // {batch, index}

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/feedback-app/${token}/villa-photos`)
      .then(r => r.ok ? r.json() : { batches: [] })
      .then(d => setBatches(d.batches || []))
      .catch(() => setBatches([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const imgUrl = (path) => `${API}/api/feedback-app/${token}/villa-photos/image?path=${encodeURIComponent(path)}`;

  if (loading && !batches.length) return <p className="text-white/60 text-xs">Loading photos…</p>;
  if (!batches.length) return <p className="text-white/60 text-xs">No readiness photos yet. Staff will upload them before check-in.</p>;

  return (
    <div className="space-y-3" data-testid="host-photos-gallery">
      {batches.map(b => {
        const villaLabel = VILLAS.find(v => v.id === b.villa_id)?.label || b.villa_id;
        const uploaded = new Date(b.uploaded_at);
        return (
          <div key={b.id} className="bg-[#0F1420] rounded-lg p-3 border border-white/5" data-testid={`photo-batch-${b.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-semibold text-white">{villaLabel}</p>
                {b.booking_guest_name ? (
                  <p className="text-[10px] text-[#F57C20]">
                    {b.booking_guest_name} · {b.booking_check_in} → {b.booking_check_out}
                  </p>
                ) : (
                  <p className="text-[10px] text-white/40">No active booking matched</p>
                )}
              </div>
              <span className="text-[9px] bg-[#F57C20]/20 text-[#F57C20] px-2 py-0.5 rounded uppercase tracking-wider">{b.count} pics</span>
            </div>
            <p className="text-[10px] text-white/40 mb-2">
              Uploaded {uploaded.toLocaleDateString()} at {uploaded.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {b.uploaded_by_phone && <> · by {b.uploaded_by_phone}</>}
            </p>
            <div className="grid grid-cols-5 gap-1">
              {b.photos.map((p, i) => (
                <button key={p.id} onClick={() => setLightbox({ batch: b, index: i })}
                  data-testid={`photo-thumb-${p.id}`}
                  className="aspect-square block bg-black/40 rounded overflow-hidden">
                  <img src={imgUrl(p.path)} alt="" loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        );
      })}

      {lightbox && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
          <button onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 rounded-full w-10 h-10 flex items-center justify-center">
            <FiX className="w-5 h-5" />
          </button>
          <img
            src={imgUrl(lightbox.batch.photos[lightbox.index].path)}
            alt="" className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-0 right-0 text-center text-white/80 text-xs">
            {lightbox.index + 1} / {lightbox.batch.count}
          </div>
        </div>
      )}
    </div>
  );
}
